import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Paper,
  Chip,
  Pagination,
  Stack,
  IconButton,
  Alert,
  Snackbar,
  InputLabel,
  Button,
  Modal,
  TextField,
  Fade,
  Backdrop,
} from '@mui/material';
import PageContainer from 'src/components/container/PageContainer';
import DashboardCard2 from 'src/components/shared/DashboardCard2';
import CheckboxInputs from 'src/components/inputs/CheckboxInputs';
import SearchImg from '../../assets/search.svg';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import { useMain } from '../hooks/useMain';
import { maskData, TrimData } from './FieldData';
import {
  Person,
  Search,
  Clear,
  Phone,
  Event,
  Send,
  Edit,
  Delete,
} from '@mui/icons-material';

const ServiceReminder = () => {
  const { getServiceRemindersByBranch, updateServiceReminder, deleteServiceReminder, activeBranchId, branchData, setShowMessage } = useMain();
  
  const [loading, setLoading] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [checkedReminders, setCheckedReminders] = useState([]);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
  });

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [editedData, setEditedData] = useState({ dueDate: '', reminderSent: false });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [reminderToDelete, setReminderToDelete] = useState(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);
    return () => clearTimeout(handler);
  }, [filters.search]);

  const fetchReminders = useCallback(async () => {
    if (!activeBranchId) return;
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(debouncedSearch && { search: debouncedSearch.trim() }),
      });

      const res = await getServiceRemindersByBranch(activeBranchId, queryParams.toString());

      if (res.statusCode === 200) {
          const apiData = res.data || []; // Access the 'data' array directly
          // Sort apiData by dueDate
          const sortedData = [...apiData].sort((a, b) => {
              // Handle cases where dueDate might be missing or invalid
              const dateA = a.dueDate ? new Date(a.dueDate) : null;
              const dateB = b.dueDate ? new Date(b.dueDate) : null;

              if (dateA && dateB) {
                  return dateA.getTime() - dateB.getTime();
              }
              if (dateA) return -1; // Place valid date before null/undefined
              if (dateB) return 1; // Place null/undefined after valid date
              return 0; // Maintain original order for two null/undefined dates
          });
          setReminders(sortedData);
          setPagination(prev => ({
              ...prev,
              totalCount: res.totalCount || 0, // Assuming totalCount is at the top level
              totalPages: res.totalPages || 0, // Assuming totalPages is at the top level
          }));
      } else {
        setShowMessage({ message: res.message, severity: 'error' });
        setReminders([]);
      }
    } catch (error) {
      setShowMessage({ message: 'Error fetching service reminders', severity: 'error' });
      setReminders([]);
    } finally {
      setLoading(false);
    }
  }, [activeBranchId, pagination.page, pagination.limit, debouncedSearch, getServiceRemindersByBranch, setShowMessage]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  useEffect(() => {
    setIsAllChecked(reminders.length > 0 && checkedReminders.length === reminders.length);
  }, [checkedReminders, reminders]);
  
  const formatToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  const handleLimitChange = (e) => {
    setPagination(prev => ({
      ...prev,
      limit: Number(e.target.value),
      page: 1,
    }));
  };

  const handleAllCheckboxChange = () => {
    if (isAllChecked) {
      setCheckedReminders([]);
    } else {
      setCheckedReminders(reminders);
    }
  };

  const handleSingleCheckboxChange = (reminderId) => {
    if (checkedReminders.some(check => check._id === reminderId)) {
      setCheckedReminders(checkedReminders.filter(check => check._id !== reminderId));
      setIsAllChecked(false);
    } else {
      const checked = reminders.find(item => item._id === reminderId);
      setCheckedReminders([...checkedReminders, checked]);
    }
  };

  const handleSendReminders = () => {
    // Logic to send reminders
    console.log('Sending reminders to:', checkedReminders);
    setSnackbar({ open: true, message: `Reminders sent to ${checkedReminders.length} clients`, severity: 'success' });
    setCheckedReminders([]);
  };

  const handleEditOpen = (reminder) => {
    setSelectedReminder(reminder);
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
    setSelectedReminder(null);
  };

  const handleSave = async () => {
    // Here you would call an API to update the reminder
    const res = await updateServiceReminder(selectedReminder._id, editedData);
    console.log(res);
    setSnackbar({ open: true, message: 'Reminder updated successfully', severity: 'success' });
    handleEditClose();
    fetchReminders(); // Refresh the list
  };

  useEffect(() => {
    if (selectedReminder) {
      setEditedData({
        dueDate: selectedReminder.dueDate,
        reminderSent: selectedReminder.reminderSent,
      });
    }
  }, [selectedReminder]);

  const handleDeleteOpen = (reminder) => {
    setReminderToDelete(reminder);
    setDeleteModalOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteModalOpen(false);
    setReminderToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (reminderToDelete) {
      const res = await deleteServiceReminder(reminderToDelete._id);
      if (res.statusCode === 200) {
        setSnackbar({ open: true, message: 'Reminder deleted successfully', severity: 'success' });
        fetchReminders(); // Refresh the list
      } else {
        setSnackbar({ open: true, message: res.message || 'Failed to delete reminder', severity: 'error' });
      }
      handleDeleteClose();
    }
  };
 const reminderGridTemplate = '50px 140px 130px 1fr 115px 100px 100px';
  const reminderGridRowStyle = {
    display: 'grid',
    gridTemplateColumns: reminderGridTemplate,
    alignItems: 'center',
    columnGap: '6px',
  };
  // Remove only leading spaces while typing (spaces between words still work)
  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value.trimStart() }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Cleans pasted text, then inserts it at the cursor position
  const handleSearchPaste = (e) => {
    e.preventDefault();
    let pasted = e.clipboardData.getData('text').trim();

    // If it looks like a phone number, remove spaces, dashes, brackets and +91
    if (/^[+\d\s\-()]+$/.test(pasted)) {
      pasted = pasted.replace(/[\s\-()]/g, '').replace(/^\+?91(?=\d{10}$)/, '');
    }

    const { selectionStart, selectionEnd, value } = e.target;
    const next = (value.slice(0, selectionStart) + pasted + value.slice(selectionEnd)).trim();
    setFilters((prev) => ({ ...prev, search: next }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Trims trailing spaces when the user leaves the box
  const handleSearchBlur = (e) => {
    const trimmed = e.target.value.trim();
    if (trimmed !== e.target.value) {
      setFilters((prev) => ({ ...prev, search: trimmed }));
    }
  };
  return (
    <PageContainer title="Service Reminders" description="List of service reminders for clients">
      <DashboardCard2>
        {loading ? (
          <ShowLoader />
        ) : (
          <>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-end', 
              mb: 3 
            }}>
              {/* Left side - Show per page */}
                <Box>
                  <InputLabel>Show per page</InputLabel>
                  <Select
                    style={{
                      borderRadius: '7px',
                      border: '0.5px solid #6174DD',
                      padding: '4px 12px',
                      background: '#FFF',
                      height: '40px',
                      width: '100px',
                    }}
                    value={pagination.limit}
                    onChange={handleLimitChange}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                    <MenuItem value={200}>200</MenuItem>
                    <MenuItem value={1000000}>All</MenuItem>
                  </Select>
                </Box>

                {/* Right side - Search */}
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                  <div style={{ width: '170px', flexShrink: 0 }} onClick={() => document.getElementById('search-input').focus()}>
                    <InputLabel>Search</InputLabel>
                    <div
                      className="search-input"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '0.5px solid #6174DD',
                        borderRadius: '7px',
                        padding: '4px 12px',
                        background: '#FFF',
                        height: '40px',
                        width: '100%',
                        maxWidth: 'none',
                        margin: 0,
                        boxSizing: 'border-box',
                      }}
                    >
                      <img src={SearchImg} alt="" style={{ marginRight: '8px' }} />
                      <input
                        id="search-input"
                        type="text"
                        placeholder="Search Clients..."
                        value={filters.search || ''}
                        onChange={handleSearchChange}
                        onPaste={handleSearchPaste}
                        onBlur={handleSearchBlur}
                        style={{
                          flex: 1,
                          border: 'none',
                          outline: 'none',
                          background: 'transparent',
                          marginLeft: '0.5rem',
                        }}
                      />
                      {filters.search && (
                        <IconButton
                          onClick={() => {
                            setFilters((prev) => ({ ...prev, search: '' }));
                            setPagination((prev) => ({ ...prev, page: 1 }));
                          }}
                          style={{ padding: 1 }}
                        >
                          <Clear
                            style={{
                              color: '#000',
                              marginRight: '-0.2rem',
                              marginTop: '0.1rem',
                              fontSize: '1.1rem',
                            }}
                          />
                        </IconButton>
                      )}
                    </div>
                  </div>
                </Box>
                </Box>

            <Box sx={{ overflow: 'auto', width: '100%' }}>
            <table className="table-container mt-3" style={{ width: '100%', minWidth: '900px', borderCollapse: 'separate', borderSpacing: '0 6px' }}>
                <thead className="table-thead">
                  <tr style={reminderGridRowStyle}>
                    <th style={{ textAlign: 'center' }}>
                      <CheckboxInputs type={'Heading'} isAllChecked={isAllChecked} handleAllCheckboxChange={handleAllCheckboxChange} />
                    </th>
                    <th style={{ textAlign: 'center' }}><Stack direction="row" spacing={1} justifyContent="center" alignItems="center"><span>Client Name</span></Stack></th>
                    <th style={{ textAlign: 'center' }}><Stack direction="row" spacing={1} justifyContent="center" alignItems="center"><span>Number</span></Stack></th>
                    <th style={{ textAlign: 'center' }}><Stack direction="row" spacing={1} justifyContent="center" alignItems="center"><span>Service</span></Stack></th>
                    <th style={{ textAlign: 'center' }}><Stack direction="row" spacing={1} justifyContent="center" alignItems="center"><span>Reminder Date</span></Stack></th>
                    <th style={{ textAlign: 'center' }}><span>Reminder Sent</span></th>
                    <th style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody className="table-tbody">
               {reminders.length > 0 ? reminders.map((item) => (
                    <tr 
                      key={item._id} 
                      style={{ 
                        ...reminderGridRowStyle,
                        transition: 'all 0.2s ease',
                        cursor: 'default'
                      }}
                    >
                      <td style={{ textAlign: 'center', padding: '4px 0 4px 15px' }}>
                        <CheckboxInputs type={'content'} handleSignleCheckboxChange={() => handleSingleCheckboxChange(item._id)} checkedItems={checkedReminders} fieldId={item?._id} />
                      </td>
                      <td style={{ 
                        textAlign: 'center', fontWeight: '600', 
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', 
                        padding: '4px 8px', color: '#1a237e'
                      }}>
                        {TrimData(item.clientName)}
                      </td>
                      <td style={{ textAlign: 'center', padding: '4px 8px' }}>
                        <Typography variant="body2" sx={{ color: '#444', fontSize: '0.8rem', fontWeight: 500 }}>
                          {maskData(item.clientNumber, branchData?.isMasked)}
                        </Typography>
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '4px 10px' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }} title={item.service?.serviceName}>
                          {item.service?.serviceName || '-'}
                        </Typography>
                      </td>
                      <td style={{ textAlign: 'center', padding: '4px 8px', fontSize: '0.8rem' }}>{formatToDDMMYYYY(item.dueDate)}</td>
                      <td style={{ textAlign: 'center', padding: '4px 8px' }}>
                        <Chip 
                          label={item.reminderSent ? 'Sent' : 'Pending'} 
                          size="small" 
                          color={item.reminderSent ? 'success' : 'warning'}
                          sx={{ fontSize: '9px', height: '18px', fontWeight: '800', textTransform: 'uppercase' }} 
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <IconButton onClick={() => handleEditOpen(item)} size="small">
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteOpen(item)} size="small">
                          <Delete fontSize="small" />
                        </IconButton>
                      </td>
                      
                    </tr>
                  )) : (
                    <tr style={{ display: 'block' }}>
                      <td style={{ textAlign: 'center', padding: '50px', display: 'block' }}>
                        <Typography color="textSecondary" variant="h6">No service reminders found.</Typography>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
          
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                color="primary"
                onChange={(e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
                disabled={pagination.totalPages <= 1}
              />
            </Box>
            
            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                {snackbar.message}
              </Alert>
            </Snackbar>
          </>
        )}
      </DashboardCard2>

      <Modal
        open={editModalOpen}
        onClose={handleEditClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={editModalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" component="h2">
              Edit Reminder for {selectedReminder?.clientName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Service: {selectedReminder?.service?.serviceName}
            </Typography>
            <TextField
              label="Due Date"
              type="date"
              fullWidth
              value={editedData.dueDate}
              onChange={(e) => setEditedData({ ...editedData, dueDate: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ mb: 2 }}
            />
            {/* <Select
              fullWidth
              value={editedData.reminderSent}
              onChange={(e) => setEditedData({ ...editedData, reminderSent: e.target.value })}
            >
              <MenuItem value={true}>Sent</MenuItem>
              <MenuItem value={false}>Pending</MenuItem>
            </Select> */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleEditClose} sx={{ mr: 1 }}>Cancel</Button>
              <Button onClick={handleSave} variant="contained">Save</Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
      <Modal
        open={deleteModalOpen}
        onClose={handleDeleteClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={deleteModalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" component="h2">
              Confirm Deletion
            </Typography>
            <Typography sx={{ mt: 2 }}>
              Are you sure you want to delete the reminder for {reminderToDelete?.clientName}?
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleDeleteClose} sx={{ mr: 1 }}>Cancel</Button>
              <Button onClick={handleDeleteConfirm} variant="contained" color="error">Delete</Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </PageContainer>
  );
};

export default ServiceReminder;
